import React, { useState, useContext } from 'react'
import { useApolloClient } from '@apollo/client'
import { Message, Grid } from 'semantic-ui-react'
import MixpanelTracker from '../utils/MixpanelTracker'
import General, { DEFAULT_GENERAL_INFO } from '../components/Forms/General'
import EEO, { DEFAULT_EEO } from '../components/Forms/EEO'
import Professional, {
  DEFAULT_PROFESSIONAL_INFO,
} from '../components/Forms/Professional'
import Expertise, { DEFAULT_EXPERTISE } from '../components/Forms/Expertise'
import RegisterLayout from '../components/Register/RegisterLayout'
import RegisterActions from '../components/Register/RegisterActions'
import Welcome from './Welcome'
import DataContext from '../contexts/DataContext'
import ExpertProfile from '../lib/ExpertProfile'
import { LOAD_ME_QUERY } from '../graphql/user'

const Register = () => {
  const apolloClient = useApolloClient()
  const { advisor } = useContext(DataContext)

  const [generalInfo, setGeneralInfo] = useState(DEFAULT_GENERAL_INFO)
  const [professionalInfo, setProfessionalInfo] = useState(
    DEFAULT_PROFESSIONAL_INFO
  )
  const [expertise, setExpertise] = useState(DEFAULT_EXPERTISE)
  const [eeo, setEEO] = useState(DEFAULT_EEO)
  const [step, setStep] = useState(0)
  const [message, setMessage] = useState(null)
  const [loading, setLoading] = useState(false)

  const register = async () => {
    const profile = {
      ...generalInfo,
      ...professionalInfo,
      ...expertise,
      ...eeo,
    }
    setLoading(true)
    try {
      await ExpertProfile.create(apolloClient, profile)
      await apolloClient.refetchQueries({ include: [LOAD_ME_QUERY] })
    } catch (err) {
      setMessage({
        header: 'We could not complete your registration',
        content: err.networkError
          ? 'Please check your connection and try again'
          : err.message,
      })
    } finally {
      setLoading(false)
    }
  }

  const showMissingFields = (content) =>
    setMessage({ header: 'Empty fields detected!', content })

  const onNext = () => {
    setMessage(null)
    switch (step) {
      case 0: {
        const errorMessage = ExpertProfile.hasInvalidFields(generalInfo)
        if (errorMessage) {
          return showMissingFields(errorMessage)
        }
        if (!advisor.timeZone) {
          return showMissingFields('Please select your timezone')
        }
        MixpanelTracker.trackRegisterGeneralStep(generalInfo)
        setStep(step + 1)
        break
      }
      case 1: {
        const errorMessage = ExpertProfile.hasInvalidFields(professionalInfo)
        if (errorMessage) {
          return showMissingFields(errorMessage)
        }
        MixpanelTracker.trackRegisterProfessionalStep(professionalInfo)
        setStep(step + 1)
        break
      }
      case 2:
        MixpanelTracker.trackRegisterExpertiseStep(expertise)
        setStep(step + 1)
        break
      case 3:
        MixpanelTracker.trackRegisterEEOStep(eeo)
        register()
        break
      default:
        break
    }
  }

  const onPrev = () => {
    setMessage(null)
    setStep(step - 1)
  }

  const renderForm = () => {
    switch (step) {
      case 0:
        return <General profile={generalInfo} setProfile={setGeneralInfo} />
      case 1:
        return (
          <Professional
            profile={professionalInfo}
            setProfile={setProfessionalInfo}
          />
        )
      case 2:
        return <Expertise profile={expertise} setProfile={setExpertise} />
      default:
        return <EEO profile={eeo} setProfile={setEEO} />
    }
  }

  if (advisor.expert && !loading) return <Welcome />

  return (
    <Grid container stackable id="main">
      <Grid className="step-progress container">
        <Grid.Row>
          <RegisterLayout step={step} />
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            {message && (
              <Message
                error
                header={message.header}
                content={message.content}
              />
            )}
            {renderForm()}
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <RegisterActions
              onNext={onNext}
              onPrev={onPrev}
              loading={loading}
              step={step}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Grid>
  )
}

export default Register
